import { useState, useEffect, useRef, useCallback } from 'react';
import {
  generateCampaignInsight,
  generateBulkEmailInsight,
  generateIndividualEmailInsight,
  generateScheduledInsight,
} from 'api/emailHistory/insightsApi';

// Session cache - in-memory Map that persists for the session
const sessionCache = new Map();

/**
 * Generate cache key from tab and filters
 * @param {string} tab - Tab identifier
 * @param {Object} filters - Filter object
 * @returns {string} Cache key
 */
function generateCacheKey(tab, filters) {
  // Sort filter keys and only include non-empty values
  const sortedFilters = Object.keys(filters)
    .sort()
    .reduce((acc, key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  
  return `${tab}:${JSON.stringify(sortedFilters)}`;
}

/**
 * Get the appropriate API function based on tab
 * @param {string} tab - Tab identifier
 * @returns {Function} API function
 */
function getApiFunction(tab) {
  switch (tab) {
    case 'campaigns':
      return generateCampaignInsight;
    case 'bulk-emails':
      return generateBulkEmailInsight;
    case 'individual':
      return generateIndividualEmailInsight;
    case 'scheduled':
      return generateScheduledInsight;
    default:
      throw new Error(`Unknown tab: ${tab}`);
  }
}

/**
 * @hook useEmailInsight
 * @description Fetches AI insights for email history with debouncing and caching
 * @param {string} tab - Tab identifier ('campaigns', 'bulk-emails', 'individual', 'scheduled')
 * @param {Object} filters - Filter parameters (tab-specific)
 * @returns {Object} Object containing insight data, loading state, error, and refetch function
 */
export function useEmailInsight(tab, filters) {
  const [insight, setInsight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Extract relevant filters based on tab
  const getRelevantFilters = useCallback(() => {
    const relevantFilters = {};
    
    // Common filters
    if (filters.dateFrom) relevantFilters.dateFrom = filters.dateFrom;
    if (filters.dateTo) relevantFilters.dateTo = filters.dateTo;
    if (filters.search) relevantFilters.search = filters.search;
    
    // Tab-specific filters
    if (tab === 'bulk-emails' || tab === 'scheduled') {
      if (filters.campaignId !== null && filters.campaignId !== undefined) {
        relevantFilters.campaignId = filters.campaignId;
      }
    }
    
    if (tab === 'individual') {
      if (filters.bulkEmailId !== null && filters.bulkEmailId !== undefined) {
        relevantFilters.bulkEmailId = filters.bulkEmailId;
      }
    }
    
    return relevantFilters;
  }, [tab, filters]);

  const fetchInsight = useCallback(async (relevantFilters, skipCache = false) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const cacheKey = generateCacheKey(tab, relevantFilters);
    
    // Check cache first (unless skipCache is true)
    if (!skipCache && sessionCache.has(cacheKey)) {
      const cachedData = sessionCache.get(cacheKey);
      setInsight(cachedData);
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // Show loading state
    setIsLoading(true);
    setError(null);
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    try {
      const apiFunction = getApiFunction(tab);
      const data = await apiFunction(relevantFilters);
      
      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }
      
      // Store in cache
      sessionCache.set(cacheKey, data);
      
      setInsight(data);
      setError(null);
    } catch (err) {
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }
      
      console.error('Error fetching insight:', err);
      
      // Handle error with code
      if (err.code === 'INSUFFICIENT_DATA') {
        setError({ code: 'INSUFFICIENT_DATA', message: err.message || 'Not enough data for insights' });
      } else {
        setError({ code: 'GENERIC', message: err.message || 'Could not generate insight' });
      }
      
      setInsight(null);
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [tab]);

  // Debounced effect for filter changes
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    const relevantFilters = getRelevantFilters();
    
    // Set up debounce timer (1 second)
    debounceTimerRef.current = setTimeout(() => {
      fetchInsight(relevantFilters);
    }, 1000);
    
    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getRelevantFilters, fetchInsight]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    const relevantFilters = getRelevantFilters();
    fetchInsight(relevantFilters, true); // Skip cache on manual refetch
  }, [getRelevantFilters, fetchInsight]);

  return {
    insight,
    isLoading,
    error,
    refetch,
  };
}

